import { UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import {
  SettingsProtocolsGetClientActiveCountriesResponse,
  SettingsProtocolsGetEmployeeListResponse,
  SettingsProtocolsGetProfileResponse,
} from '~/swagger/data-contracts';
import { queryClient, legacySettingsApi, settingsApi } from '~/apis';
import { QueryKeyAuth } from '~/apis/auth';

export const QueryKeySettings = 'settings';

export const useGetProfileQuery = (
  options: UseQueryOptions<SettingsProtocolsGetProfileResponse, null, SettingsProtocolsGetProfileResponse>
) => {
  return useQuery<SettingsProtocolsGetProfileResponse, null, SettingsProtocolsGetProfileResponse>({
    queryKey: [QueryKeySettings, 'profile'],
    queryFn: async () => {
      const { data } = await settingsApi.getProfileList();
      return data;
    },
    ...options,
  });
};

export const useUpdateCompanyInfoMutation = () => {
  return useMutation({
    mutationFn: settingsApi.updateCompanyInfoCreate,
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeyAuth, 'user-info']);
      queryClient.invalidateQueries([QueryKeySettings, 'profile']);
    },
  });
};

export const usePresignedUploadMutation = () =>
  useMutation({
    mutationFn: settingsApi.presignedUploadCreate,
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeySettings, 'profile']);
    },
  });

export const useGetEmployeeListQuery = <TData>(
  options?: UseQueryOptions<SettingsProtocolsGetEmployeeListResponse, unknown, TData>
) => {
  return useQuery<SettingsProtocolsGetEmployeeListResponse, unknown, TData>({
    queryKey: [QueryKeySettings, 'employee', 'list'],
    queryFn: async () => {
      const { data } = await settingsApi.getEmployeeListList();
      return data;
    },
    ...options,
  });
};

export const useGetClientActiveCountriesQuery = <TData>(
  options?: UseQueryOptions<SettingsProtocolsGetClientActiveCountriesResponse, unknown, TData>
) => {
  return useQuery<SettingsProtocolsGetClientActiveCountriesResponse, unknown, TData>({
    queryKey: [QueryKeySettings, 'employee', 'countries'],
    queryFn: async () => {
      const { data } = await settingsApi.clientActiveCountriesList();
      return data;
    },
    ...options,
  });
};

export const useSendInvitationMutation = () => {
  return useMutation({
    mutationFn: legacySettingsApi.settingsSendInvitationCreate,
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeySettings, 'employee']);
    },
  });
};

export const useResendInvitationMutation = () => {
  return useMutation({
    mutationFn: legacySettingsApi.settingsRequestInvitationEmailCreate,
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeySettings, 'employee']);
    },
  });
};

export const useUpdateEmployeeRoleMutation = () => {
  return useMutation({
    mutationFn: legacySettingsApi.settingsUpdateEmployeeRoleCreate,
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeySettings, 'employee']);
    },
  });
};

export const useDeleteEmployeeMutation = () => {
  return useMutation({
    mutationFn: legacySettingsApi.settingsDeleteEmployeeCreate,
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeySettings, 'employee']);
    },
  });
};
